import { inputVariants } from '@/app/common/components/input/inputVariants'
import DefaultAvatar from '@/app/pages/profile/components/DefaultAvatar'
import { currencyFormatter } from '@/lib/utils'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import { Textarea } from '@/shad/ui/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { DollarSign, FileText, User } from 'lucide-react'
import CurrencyInput from 'react-currency-input-field'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import FormButtons from '../../_components/form-buttons'
import { StudentAwardInformation } from '../../award-form'

// Zod schema for form validation
const createManualAwardSchema = ({
  maxAward,
  initialAward,
}: {
  maxAward: number
  initialAward: number
}) =>
  z.object({
    amount: z
      .string()
      .min(1, 'Please enter an amount')
      .refine((val) => {
        const num = parseFloat(val)
        return !isNaN(num) && num > 0
      }, 'Please enter a valid amount greater than $0')
      .refine(
        (val) => {
          const num = parseFloat(val)
          if (Number.isNaN(num)) return false
          if (initialAward > maxAward && num === initialAward) return true
          return num <= maxAward
        },
        `Amount cannot exceed available purse balance of ${currencyFormatter.format(maxAward)}`
      ),
    reason: z
      .string()
      .min(1, 'Please provide a reason for this manual award')
      .min(10, 'Reason must be at least 10 characters long')
      .max(500, 'Reason must be less than 500 characters'),
  })

type ManualAwardFormData = z.infer<ReturnType<typeof createManualAwardSchema>>

interface ManualAwardStepProps {
  student: StudentAwardInformation
  onSave: (amount: number, reason: string) => void
  onCancel: () => void
  initialAmount?: number
  initialReason?: string
  totalLeftInPurse: number
  totalToBeAwarded: number
}

export default function ManualAwardStep({
  student,
  onSave,
  onCancel,
  initialAmount = 0,
  initialReason = '',
  totalLeftInPurse,
  totalToBeAwarded,
}: ManualAwardStepProps) {
  const otherAwardsTotal = totalToBeAwarded - initialAmount
  const maxAwardFromBalance = totalLeftInPurse - otherAwardsTotal
  const maxAward =
    maxAwardFromBalance >= 0 ? maxAwardFromBalance : Math.max(initialAmount, 0)
  const form = useForm<ManualAwardFormData>({
    resolver: zodResolver(
      createManualAwardSchema({
        maxAward,
        initialAward: initialAmount,
      })
    ),
    defaultValues: {
      amount: initialAmount.toString(),
      reason: initialReason,
    },
  })

  const watchedAmount = form.watch('amount')
  const watchedReason = form.watch('reason')
  const numericAmount = parseFloat(watchedAmount) || 0

  const onSubmit = (data: ManualAwardFormData) => {
    const numericAmount = parseFloat(data.amount)
    onSave(numericAmount, data.reason.trim())
  }

  const handleCancel = () => {
    onCancel()
  }

  const baseAmount = student.base_award_amount
  const modifierAmount = student.modifier_total

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
            <DollarSign className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Manual Award Amount
            </h2>
            <p className="text-sm text-gray-500">
              Set a custom award amount for this student
            </p>
          </div>
        </div>
        {/* Student Information Card */}
        <div className="bg-white rounded-xl px-2  ">
          <div className="flex items-center gap-3">
            <DefaultAvatar
              containerClassName="px-0"
              variant="neutral"
              firstName={student.first_name}
              lastName={student.last_name}
            />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-base">
                {student.first_name} {student.last_name} - ID: {student.id}
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>{student.applciant_email}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 mb-1">
                Amount already Awarded
              </div>
              <div className="font-semibold text-gray-900">
                {currencyFormatter.format(student.total_awarded_amount)}
              </div>
            </div>
          </div>
        </div>
        {/* Purse Balance Information */}
        {/* Form Fields */}
        <div className="space-y-4">
          {/* Amount Input */}
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-900">
                  Award Amount <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <CurrencyInput
                      prefix={'$'}
                      type="text"
                      placeholder="0.00"
                      onBlur={field.onBlur}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value)
                      }}
                      className={inputVariants({
                        variant: 'text',
                      })}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Reason Textarea */}
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-900">
                  Reason for Manual Award{' '}
                  <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <FileText className="h-4 w-4 text-gray-400" />
                    </div>
                    <Textarea
                      {...field}
                      placeholder="Explain why this student should receive a manual award amount..."
                      rows={4}
                      className="pl-10"
                    />
                  </div>
                </FormControl>
                <p className="text-xs text-gray-500 mt-1">
                  {watchedReason.length}/500 characters
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* Summary */}
        <div className=" ">
          <div className="flex items-center gap-2 mb-2">
            <User className="h-4 w-4 text-gray-900" />
            <span className="text-sm font-medium text-gray-700">
              Award Summary
            </span>
          </div>
          <div className="text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Student:</span>
              <span className="font-medium">
                {student.first_name} {student.last_name}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Base Award:</span>
              <span className="font-medium text-gray-800 line-through">
                {currencyFormatter.format(baseAmount)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Additional Modifier Award:</span>
              <span className="font-medium text-gray-800 line-through">
                {currencyFormatter.format(modifierAmount)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Manual Award:</span>
              <span className="font-medium text-gray-800">
                {currencyFormatter.format(numericAmount)}
              </span>
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        <FormButtons>
          <FormButtons.BackButton onClick={handleCancel} />
          <FormButtons.NextButton type="submit" />
        </FormButtons>{' '}
      </form>
    </Form>
  )
}
